import { useState } from 'react';
import {
  Avatar,
  AvatarGroup,
  Popover,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
  Box,
  Chip,
  Tooltip,
  Link,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import { motion } from 'framer-motion';
import { useCatch } from '../../reactHelper';
import { useTranslation } from '../../common/components/LocalizationProvider';
import fetchOrThrow from '../../common/util/fetchOrThrow';

const DeviceUsersValue = ({ deviceId }) => {
  const t = useTranslation();
  const [users, setUsers] = useState();
  const [anchorEl, setAnchorEl] = useState(null);

  // ===============================
  // 🔹 Fetch users by deviceId
  // ===============================
  const loadUsers = useCatch(async () => {
    const query = new URLSearchParams({ deviceId });
    const response = await fetchOrThrow(`/api/users?${query.toString()}`);
    setUsers(await response.json());
  });

  const handleOpen = (event) => {
    if (!users) {
      loadUsers().then(() => setAnchorEl(event.currentTarget));
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => setAnchorEl(null);
  const open = Boolean(anchorEl);

  // ===============================
  // 🎨 Avatar color helper
  // ===============================
  const getUserColor = (name) => {
    const palette = ['#90caf9', '#ce93d8', '#80cbc4', '#ffb74d', '#e57373', '#64b5f6'];
    const idx = name ? name.charCodeAt(0) % palette.length : 0;
    return palette[idx];
  };

  const getUserAvatar = (user) =>
    user.photo && user.photo.length < 50 ? `/photos/users/${user.photo}` : null;

  // ===============================
  // 🧠 Conditional Render States
  // ===============================
  if (!users) {
    return (
      <Link
        href="#"
        onClick={(e) => {
          e.preventDefault();
          loadUsers();
        }}
        sx={{ fontSize: '0.85rem', opacity: 0.8 }}
      >
        {t('reportShow')}
      </Link>
    );
  }

  if (users.length === 0) {
    return (
      <Chip
        label={t('sharedNoData')}
        size="small"
        variant="outlined"
        icon={<PeopleIcon fontSize="small" />}
        sx={{
          opacity: 0.6,
          borderColor: 'rgba(255,255,255,0.2)',
          color: 'rgba(255,255,255,0.7)',
        }}
      />
    );
  }

  // ===============================
  // 💎 Main Render
  // ===============================
  return (
    <>
      <Box
        onClick={handleOpen}
        sx={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          '&:hover': { opacity: 0.85 },
        }}
      >
        <Tooltip title={`${users.length} ${t('settingsUsers').toLowerCase()}`}>
          <AvatarGroup
            max={3}
            spacing="small"
            sx={{
              '& .MuiAvatar-root': {
                width: 28,
                height: 28,
                fontSize: '0.75rem',
                border: '1px solid rgba(255,255,255,0.25)',
                boxShadow: '0 0 4px rgba(0,0,0,0.3)',
              },
            }}
          >
            {users.map((user) => (
              <Tooltip key={user.id} title={user.name}>
                <Avatar
                  alt={user.name}
                  src={getUserAvatar(user)}
                  sx={{
                    bgcolor: getUserColor(user.name),
                    width: 28,
                    height: 28,
                    fontSize: '0.8rem',
                    color: '#fff',
                  }}
                >
                  {user.name?.charAt(0)?.toUpperCase() ?? '?'}
                </Avatar>
              </Tooltip>
            ))}
          </AvatarGroup>
        </Tooltip>

        {users.length > 3 && (
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
            +{users.length - 3}
          </Typography>
        )}
      </Box>

      {/* ===============================
          🌟 Popover - Clean Corporate Style
      =============================== */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            mt: 1,
            boxShadow: '0 6px 25px rgba(0,0,0,0.45)',
            borderRadius: 2,
            bgcolor: 'rgba(30,30,30,0.98)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.08)',
            minWidth: 300,
            maxHeight: 420,
            overflow: 'hidden',
          },
        }}
      >
        <Box
          sx={{
            p: 2,
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            position: 'sticky',
            top: 0,
            bgcolor: 'rgba(30,30,30,0.98)',
            zIndex: 1,
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{ color: 'rgba(255,255,255,0.85)' }}
          >
            Usuários Vinculados ({users.length})
          </Typography>
        </Box>

        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
        >
          <List
            dense
            disablePadding
            sx={{
              px: 2,
              py: 1,
              overflowY: 'auto',
              maxHeight: 360,
              '&::-webkit-scrollbar': { width: 0 },
            }}
          >
            {users.map((user) => (
              <ListItem
                key={user.id}
                sx={{
                  px: 0,
                  py: 0.75,
                  borderBottom: '1px solid rgba(255,255,255,0.05)',
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    alt={user.name}
                    src={getUserAvatar(user)}
                    sx={{
                      width: 34,
                      height: 34,
                      bgcolor: getUserColor(user.name),
                      fontSize: '0.9rem',
                      color: '#fff',
                    }}
                  >
                    {user.name?.charAt(0)?.toUpperCase() ?? '?'}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={user.name}
                  secondary={user.email || ''}
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: 600,
                    color: 'rgba(255,255,255,0.9)',
                  }}
                  secondaryTypographyProps={{
                    variant: 'caption',
                    color: 'rgba(255,255,255,0.6)',
                    noWrap: true,
                  }}
                />
              </ListItem>
            ))}
          </List>
        </motion.div>
      </Popover>
    </>
  );
};

export default DeviceUsersValue;
